import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './StoryboardWidget.module.scss';
import { CustomButton } from '../../../../components/Button/Button';
import { useTranslation } from 'react-i18next';
import SelectOption from '../../../../components/SelectOption/SelectOption';
import HomeButton from '../HomeButton/HomeButton';
import { DebounceInput } from 'react-debounce-input';
import cx from 'classnames';
import HomePageSectionHeader from '../HomePageSectionHeader/HomePageSectionHeader';
import { toCamelCase } from '../../../../utils/stringHelper';
import TypebotEmbed from '../TypebotEmbed/TypebotEmbed';
import configs from '../../../../../../conf';
import { useUserContext } from '../../../../context/UserContext';
import SearchComponent from '../../../../components/SearchComponent/SearchComponent';

const config = configs[process.env.APP_CONF];

const MIN_REQUEST_LENGTH = 10;

const StoryboardWidget = ({ className }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const { userCompanyId, user } = useUserContext();

  const [searchValue, setSearchValue] = useState('');
  const [searchState, setSearchState] = useState(false);

  const [showTypebot, setShowTypebot] = useState(false);

  const mostPopularList = useMemo(() => {
    const list = [];
    const mostPopularNames = data?.mostPopularTypes?.map(item => item.name);
    data &&
      data?.useCases?.forEach(useCase => {
        const filteredFromCategory = useCase.types.filter(type => mostPopularNames.includes(type.name));
        if (filteredFromCategory.length) {
          list.push(...filteredFromCategory.map(type => ({ ...type, categoryColor: useCase.categoryColor })));
        }
      });
    return list;
  }, [data]);

  const getContentByLanguage = useCallback((data) => {
    return data[language] ?? data['en'];
  }, [language]);

  const options = useMemo(() => {
    const optionsList = [{ id: 'mostPopular', name: t('home_page.storyboard_widget.most_popular') }];
    if (data?.useCases) {
      const preparedCategories = data?.useCases?.map(useCase => ({
        id: useCase.category,
        name: getContentByLanguage(useCase.categoryTitle),
        categoryColor: useCase.categoryColor,
      }));
      preparedCategories && optionsList.push(...preparedCategories);
    }
    return optionsList;
  }, [data, language, t, getContentByLanguage]);

  const contentRef = useRef(null);
  const inputRef = useRef(null);

  const [currentCategoryId, setCurrentCategoryId] = useState(options[0]?.id);

  const currentCategory = useMemo(() => options.find(item => item.id == currentCategoryId), [options, currentCategoryId]);

  const [currentUseCase, setCurrentUseCase] = useState(null);
  const [showInput, setShowInput] = useState(true);
  const [customRequest, setCustomRequest] = useState('');

  const useCases = useMemo(() => {
    if (searchValue?.trim()?.length) {
      const filteredTypes = data?.useCases?.map(({ types, categoryColor }) => {
        return types?.filter(({ title }) => title[language]?.toLowerCase().includes(searchValue?.toLowerCase())).map(type => ({ ...type, categoryColor }));
      });
      return filteredTypes.flat();
    }
    if (currentCategoryId === 'mostPopular') {
      return mostPopularList;
    }
    return data?.useCases?.find(useCase => useCase.category === currentCategoryId)?.types;
  }, [data, currentCategoryId, mostPopularList, searchValue]);

  const isNextAllowed = useMemo(() => currentUseCase || customRequest.length > MIN_REQUEST_LENGTH, [currentUseCase, customRequest]);

  const handleCustomRequestSelected = () => {
    setShowInput(true);
    setCurrentUseCase(null);
    inputRef.current.focus();
  };

  const handleSelectUseCase = useCase => {
    setShowInput(false);
    setCurrentUseCase(useCase);
  };

  const handleUpdateCustomRequest = e => {
    setCustomRequest(e.target.value);
  };

  const handleNextClicked = () => {
    setShowTypebot(prevState => !prevState);
  };

  const handleSelectSection = useCallback(category => {
    setCurrentCategoryId(category?.id);
    setCurrentUseCase(null);
    setShowInput(true);
  }, []);

  useEffect(() => {
    const fetchJsonData = async () => {
      const timestamp = new Date().getTime();
      const resp = await fetch(`${config.typebotAssistantDbJsonUrl}?t=${timestamp}`);
      const responseData = await resp.json();
      setData(responseData);
    };

    fetchJsonData();

    return () => {
      setShowTypebot(false);
      setCustomRequest('');
    }
  }, []);

  useEffect(() => {
    const assitantChatVariables = {
      idea_answer: customRequest,
      template_answer: currentUseCase?.name ?? null,
      request_language: language,
      assistant_db_json_url: config.typebotAssistantDbJsonUrl,
      company_id: userCompanyId,
      user_name: user?.userFullName
    };

    sessionStorage.setItem('assistantChatVariables', JSON.stringify(assitantChatVariables));
  }, [customRequest, currentUseCase, language]);

  return (
    <div className={cx(styles.wrapper, className)}>
      <div className={styles.header}>
        <HomePageSectionHeader
          icon="scriptGenerator"
          title={t('home_page.script_generator')}
          description={t('home_page.script_generator.description')}
        />
        <CustomButton
          className={styles.nextBtn}
          label={t(showTypebot ? 'general.cancel' : 'storyboard.editor.next')}
          variant="basic"
          disabled={!isNextAllowed}
          onClick={handleNextClicked}
        />
      </div>
      {showTypebot ? (
        <TypebotEmbed />
      ) : (
        <div className={styles.content}>
          <div ref={contentRef} className={styles.selectionContainer}>
            <SelectOption
              parentRef={contentRef}
              currentlySelected={currentCategory}
              options={options}
              onSelect={handleSelectSection}
              currentInputClassName={styles.templateInputSelector}
              inputWidthByContent={true}
              selectedIcon={'checkSelectOption'}
              className={cx(styles.selectOption, { [styles.hide]: searchState })}
            />
              <SearchComponent searchState={setSearchState} inputClassName={styles.inputSearchComponent} searchValue={searchValue} onChange={setSearchValue} clearButton={true} />
          </div>
          <div className={styles.useCaseOptionsList}>
            {useCases?.map((useCase, index) => (
              <HomeButton
                key={`${useCase.name}_${index}`}
                label={getContentByLanguage(useCase.title)}
                icon={toCamelCase(useCase?.style)}
                onClick={() => handleSelectUseCase(useCase)}
                color={useCase?.categoryColor ?? currentCategory?.categoryColor}
                info={getContentByLanguage(useCase.description)}
                className={cx(styles.useCaseButton, { [styles.selected]: currentUseCase?.name === useCase.name })}
                searchValue={searchValue.trim()}
              />
            ))}
            <HomeButton
              label={'home_page.storyboard_widget.no_template'}
              icon={'noTemplate'}
              onClick={handleCustomRequestSelected}
              color="rgba(208, 198, 221, 1)"
              className={cx(styles.customRequestButton, { [styles.selected]: showInput })}
              keepIconColor={true}
            />
          </div>
          <DebounceInput
            inputRef={inputRef}
            debounceTimeout={300}
            type="text"
            element={'textarea'}
            value={customRequest}
            onChange={handleUpdateCustomRequest}
            className={cx(styles.inputContainer, { [styles.show]: showInput })}
            placeholder={t('home_page.storyboard_widget.custom_request.placeholder')}
          />
        </div>
      )}
    </div>
  );
};

export default StoryboardWidget;
